import MapErrorBoundary from "@/components/maps/MapErrorBoundary"
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable"
import FleetOverviewMap, { FleetOverviewMapRef } from "./FleetOverviewMap"
import { useAppDispatch, useAppSelector } from "@/reducers/hooks"
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import DriversList, { DriversListRef } from "./DriversList"
import {
  DriverDisplayInfo,
  selectAllDriverRouteAndLocationDetailsMap,
  selectAllDriversJids,
  selectDriverRouteAndLocationDetailsMap,
} from "@/reducers/accountChatsSelectors"
import SearchBar, { DispatcherFilterOption, FilterConfig } from "./SearchBar"
import { useUserPermissions } from "@/hooks/useUserPermissions"
import {
  selectDispatcherChats,
  selectDispatcherChatsLoaded,
} from "@/reducers/dispatcherChatsSlice"
import { fetchDispatcherChats } from "@/reducers/dispatcherChatsThunks"
import { officeProfiles } from "@/reducers/profilesSliceSelectors"
import {
  buildDispatcherOptions,
  UNASSIGNED_SECTION_JID,
} from "@/components/chatList/dispatcherSections"
import { isMobile } from "react-device-detect"
import XmppVCardApi from "@/api/xmppVCardApi"
import { upsertProfile } from "@/reducers/profilesSlice"
import { addBookmark } from "@/api/xeps/PepNativeBookmarks"
import { joinChat } from "@/reducers/chatsSliceThunks"
import { XmppContext } from "@/stream/xmppClient"
import { selectAccountChats } from "@/reducers/accountChatsSlice"
import { TabType } from "@/reducers/tabsSlice"
import {
  selectTrailerLocations,
  TrailerLocation,
} from "@/reducers/trailerLocationsSlice"
import { useTranslation } from "react-i18next"

const FLEET_FILTER_STORAGE_KEY = "fleetOverviewFilterConfig"

const saveFilterConfig = (config: FilterConfig) => {
  try {
    localStorage.setItem(FLEET_FILTER_STORAGE_KEY, JSON.stringify(config))
  } catch (error) {
    console.warn("Failed to save filter config to localStorage:", error)
  }
}

const DEFAULT_FILTER_CONFIG: FilterConfig = {
  selected: [],
  showAllCars: false,
  showTrailers: true,
}

const loadFilterConfig = (): FilterConfig => {
  try {
    const stored = localStorage.getItem(FLEET_FILTER_STORAGE_KEY)
    if (stored) {
      // Configs saved before a key existed fall back to that key's default
      return { ...DEFAULT_FILTER_CONFIG, ...JSON.parse(stored) }
    }
  } catch (error) {
    console.warn("Failed to load filter config from localStorage:", error)
  }
  return DEFAULT_FILTER_CONFIG
}

type FleetOverviewProps = {
  onClose: () => void
  onNavigateToChat: (jid: string, tab?: TabType) => void
}

const FleetOverview = ({ onClose, onNavigateToChat }: FleetOverviewProps) => {
  const { t } = useTranslation()
  const [selectedDriver, setSelectedDriver] =
    useState<DriverDisplayInfo | null>(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [filterConfig, setFilterConfig] =
    useState<FilterConfig>(loadFilterConfig)
  const [hoveredDriver, setHoveredDriver] = useState<string | null>(null)
  const driversListRef = useRef<DriversListRef>(null)
  const fleetOverviewMapRef = useRef<FleetOverviewMapRef>(null)

  const [isFilterOpen, setIsFilterOpen] = useState(false)

  const dispatch = useAppDispatch()

  const myDriversMap = useAppSelector(selectDriverRouteAndLocationDetailsMap)
  const allDriversMap = useAppSelector(
    selectAllDriverRouteAndLocationDetailsMap,
  )
  const allDriverJids = useAppSelector(selectAllDriversJids)
  const profiles = useAppSelector((state) => state.profiles)
  const trailerLocations = useAppSelector(selectTrailerLocations)

  const { client, myJid } = useContext(XmppContext)

  const accountChats = useAppSelector(selectAccountChats)

  // A fleet manager keeps no vehicles, so "my cars" means nothing to them:
  // they see everyone's, sliced by dispatcher.
  const { isFleetManager, membershipLocked } = useUserPermissions()
  const byDispatcher = useAppSelector(selectDispatcherChats)
  // Until the mapping arrives every card would read "Unassigned", so the
  // labels and the dispatcher filter wait for it.
  const dispatcherChatsLoaded = useAppSelector(selectDispatcherChatsLoaded)
  const showDispatchers = isFleetManager && dispatcherChatsLoaded
  const dispatcherProfiles = useAppSelector(officeProfiles)
  const dispatcherNames = useMemo(
    () =>
      Object.fromEntries(
        dispatcherProfiles.map((profile) => [profile.jid, profile.name]),
      ),
    [dispatcherProfiles],
  )
  const dispatcherOptions = useMemo<DispatcherFilterOption[]>(
    () =>
      showDispatchers
        ? buildDispatcherOptions({
            byDispatcher,
            dispatcherNames,
            myJid: myJid.bare().toString(),
          })
        : [],
    [showDispatchers, byDispatcher, dispatcherNames, myJid],
  )
  useEffect(() => {
    if (isFleetManager) dispatch(fetchDispatcherChats(client))
  }, [isFleetManager, client, dispatch])

  /** Chat JID -> the dispatcher who keeps it, for the card label and the
   * dispatcher filter. */
  const dispatcherByChat = useMemo(() => {
    const found: Record<string, string> = {}
    for (const [dispatcherJid, chatJids] of Object.entries(byDispatcher)) {
      for (const chatJid of chatJids) found[chatJid] = dispatcherJid
    }
    return found
  }, [byDispatcher])

  const showAllCars = filterConfig.showAllCars || isFleetManager
  // Choose the appropriate drivers map based on showAllCars setting
  const driversMap = showAllCars ? allDriversMap : myDriversMap

  // Fetch missing profiles for all drivers when component mounts or when showAllCars changes
  useEffect(() => {
    const fetchMissingProfiles = async () => {
      if (!window.XMPP) return

      // Get driver JIDs that need profile fetching based on current setting
      const relevantDriverJids = showAllCars
        ? Array.from(allDriverJids.keys())
        : Object.keys(myDriversMap)

      // Also fetch vCards for the actually-assigned drivers (driverA), since
      // the fleet card shows that profile — not the vehicle account's vCard.
      const relevantAccountChatJids = showAllCars
        ? Array.from(allDriverJids.values())
        : Object.values(myDriversMap)
            .map((d) => d.chatJid)
            .filter((jid): jid is string => Boolean(jid))
      const driverAJids = relevantAccountChatJids
        .map((jid) => accountChats[jid]?.driverA)
        .filter((jid): jid is string => Boolean(jid))

      const jidsToFetch = Array.from(
        new Set([...relevantDriverJids, ...driverAJids]),
      )

      // Find drivers that don't have profiles in store
      const missingProfileJids = jidsToFetch.filter(
        (jid) => !profiles.find((profile) => profile.jid === jid),
      )

      // Fetch profiles for drivers that don't have them
      for (const jid of missingProfileJids) {
        try {
          const vcard = await XmppVCardApi.fetchVCard(window.XMPP!, jid)
          if (vcard) {
            dispatch(upsertProfile({ profile: vcard }))
          }
        } catch (error) {
          console.warn(`Failed to fetch profile for driver ${jid}:`, error)
        }
      }
    }

    fetchMissingProfiles()
  }, [
    showAllCars,
    allDriverJids,
    myDriversMap,
    profiles,
    accountChats,
    dispatch,
  ])

  useEffect(() => {
    if (selectedDriver && driversListRef.current) {
      driversListRef.current.scrollToDriver(selectedDriver.jid)
    }
  }, [selectedDriver])

  const allDrivers = useMemo(() => {
    const everyone = Object.values(driversMap || {})
    const picked = filterConfig.dispatcherJid
    if (!showDispatchers || !picked) return everyone
    return everyone.filter((driver) => {
      const keeper = driver.chatJid
        ? dispatcherByChat[driver.chatJid]
        : undefined
      return picked === UNASSIGNED_SECTION_JID
        ? keeper === undefined
        : keeper === picked
    })
  }, [
    driversMap,
    filterConfig.dispatcherJid,
    showDispatchers,
    dispatcherByChat,
  ])

  const dispatcherLabelOf = useCallback(
    (driver: DriverDisplayInfo): string | undefined => {
      if (!showDispatchers || !driver.chatJid) return undefined
      const keeper = dispatcherByChat[driver.chatJid]
      if (!keeper) return t("fleet.fleetOverview.unassigned")
      return dispatcherNames[keeper] || keeper.split("@")[0]
    },
    [showDispatchers, dispatcherByChat, dispatcherNames, t],
  )

  // Fuzzy search function
  const fuzzyMatch = (text: string, query: string): boolean => {
    if (!query) return true
    if (!text) return false

    const textLower = text.toLowerCase()
    const queryLower = query.toLowerCase()

    // Simple fuzzy matching: check if all characters of query exist in text in order
    let queryIndex = 0
    for (
      let i = 0;
      i < textLower.length && queryIndex < queryLower.length;
      i++
    ) {
      if (textLower[i] === queryLower[queryIndex]) {
        queryIndex++
      }
    }
    return queryIndex === queryLower.length
  }

  // Helper function to check if driver matches selected filters
  const matchesFilters = useCallback(
    (driver: DriverDisplayInfo): boolean => {
      if (filterConfig.selected.length === 0) return true // No filters selected, show all

      return filterConfig.selected.some((filter: string) => {
        switch (filter) {
          case "with-stops":
            return driver.nextStop
          case "without-stops":
            return !driver.nextStop
          case "driving":
            return driver.isDriving
          case "not-driving":
            return !driver.isDriving
          case "on-route":
            return driver.isTracked && !driver.isOffRoute
          case "off-route":
            return driver.isTracked && driver.isOffRoute
          case "with-delay":
            return driver.showAlert && driver.isLate
          default:
            return false
        }
      })
    },
    [filterConfig.selected],
  )

  // Filter drivers based on search query and selected filters
  const drivers = useMemo(() => {
    const filtered = allDrivers.filter((driver) => {
      const name = driver.name || ""
      const plates = driver.plates || ""
      const carLabel = driver.carLabel || ""

      // Check if driver matches text search
      const matchesSearch =
        fuzzyMatch(name, searchQuery) ||
        fuzzyMatch(plates, searchQuery) ||
        fuzzyMatch(carLabel, searchQuery)

      // Check if driver matches selected filters
      const matchesFilter = matchesFilters(driver)

      return matchesSearch && matchesFilter
    })

    // Sort by name for consistency. Numeric, because names often start with
    // the side number, and "2, 26, 3, 30" put truck 3 after truck 26.
    filtered.sort((a, b) => {
      const nameA = a.name || ""
      const nameB = b.name || ""
      return nameA.localeCompare(nameB, undefined, { numeric: true })
    })

    return filtered
  }, [allDrivers, searchQuery, matchesFilters])

  // Trailers only answer to the text search; the driver filters (driving,
  // on route, delays...) mean nothing for an asset with no driver.
  const trailers = useMemo(() => {
    if (!filterConfig.showTrailers) return []
    return Object.values(trailerLocations)
      .filter(
        (trailer): trailer is TrailerLocation & { label: string } =>
          Boolean(trailer.location?.lat && trailer.location?.lon) &&
          fuzzyMatch(trailer.label || "", searchQuery),
      )
      .sort((a, b) => (a.label || "").localeCompare(b.label || ""))
  }, [trailerLocations, searchQuery, filterConfig.showTrailers])

  const handleSelectDriver = (driver: DriverDisplayInfo | null) => {
    setSelectedDriver(driver)
  }

  const handleCloseFilter = useCallback(() => {
    if (isFilterOpen) {
      setIsFilterOpen(false)
    } else {
      onClose()
    }
  }, [isFilterOpen, onClose])

  const handleSelectDriverAndShowPopup = (driver: DriverDisplayInfo) => {
    setSelectedDriver(driver)
    // Trigger popup display through ref
    if (fleetOverviewMapRef.current) {
      fleetOverviewMapRef.current.showDriverPopup(driver)
    }
  }

  const handleFlyToDriver = (driver: DriverDisplayInfo) => {
    if (fleetOverviewMapRef.current) {
      fleetOverviewMapRef.current.flyToDriver(driver, 15)
    }
  }

  const handleSelectTrailer = (trailer: TrailerLocation) => {
    fleetOverviewMapRef.current?.showTrailerPopup(trailer)
  }

  const handleFlyToTrailer = (trailer: TrailerLocation) => {
    fleetOverviewMapRef.current?.flyToTrailer(trailer, 13)
  }

  const handleSearchChange = (query: string) => {
    setSearchQuery(query)
  }

  const handleFilterChange = (config: FilterConfig) => {
    setFilterConfig(config)
    saveFilterConfig(config)
  }

  const handleNavigateToChat = useCallback(
    async (jid: string, shouldJoin: boolean = false) => {
      onClose()
      // A dispatcher on a managed account cannot pick up a chat here either.
      if (shouldJoin && membershipLocked) return
      if (shouldJoin) {
        const chat = accountChats[jid]
        if (chat) {
          await addBookmark(client, jid, chat.name)
          window.analytics.track("AddBookmark", {
            chatJid: jid,
            chatName: chat?.name,
            from: "FleetOverview",
          })
          dispatch(joinChat(client, chat))
        }
      }
      onNavigateToChat(jid, "chats")
    },
    [
      accountChats,
      client,
      dispatch,
      onClose,
      onNavigateToChat,
      membershipLocked,
    ],
  )

  return (
    <div
      className={`${isMobile ? "" : "left-[49px] w-[calc(100vw-48px)]"} fixed inset-0 z-20 h-screen bg-[var(--panel-color)]`}
    >
      <ResizablePanelGroup
        direction="horizontal"
        className="max-w-screen min-h-screen"
      >
        {!isMobile && (
          <ResizablePanel
            className="min-w-[380px]"
            defaultSize={20}
            minSize={15}
            maxSize={50}
          >
            <div className="flex h-full flex-col">
              <div className="flex-shrink-0">
                <div className="flex flex-col gap-3.5 p-2">
                  <h1 className="flex items-center p-2 text-2xl font-bold">
                    {t("fleet.fleetOverview.title")}
                  </h1>
                </div>
                <div className="p-2">
                  <SearchBar
                    searchQuery={searchQuery}
                    onSearchChange={handleSearchChange}
                    filterConfig={filterConfig}
                    onFilterChange={handleFilterChange}
                    isFilterOpen={isFilterOpen}
                    onFilterClick={() => setIsFilterOpen(!isFilterOpen)}
                    dispatchers={dispatcherOptions}
                  />
                </div>
              </div>
              <div className="flex-1 overflow-hidden pb-4">
                <DriversList
                  ref={driversListRef}
                  drivers={drivers}
                  trailers={trailers}
                  onSelectTrailer={handleSelectTrailer}
                  onFlyToTrailer={handleFlyToTrailer}
                  onSelectDriver={handleSelectDriverAndShowPopup}
                  onFlyToDriver={handleFlyToDriver}
                  onNavigateToChat={handleNavigateToChat}
                  selectedDriver={selectedDriver}
                  hoveredDriver={hoveredDriver}
                  onHoverDriver={setHoveredDriver}
                  dispatcherLabelOf={dispatcherLabelOf}
                />
              </div>
            </div>
          </ResizablePanel>
        )}
        {!isMobile && <ResizableHandle className="z-[1000]" withHandle />}
        <ResizablePanel defaultSize={60}>
          <div className="relative z-50 flex h-full items-center justify-center">
            <MapErrorBoundary>
              <FleetOverviewMap
                ref={fleetOverviewMapRef}
                drivers={drivers}
                trailers={trailers}
                onClose={onClose}
                selectedDriver={selectedDriver}
                hoveredDriver={hoveredDriver}
                onDriverSelect={handleSelectDriver}
                onHoverDriver={setHoveredDriver}
                onNavigateToChat={handleNavigateToChat}
                onCloseFilter={handleCloseFilter}
              />
            </MapErrorBoundary>
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  )
}

export default FleetOverview
